
import { Component, Vue, Watch } from 'vue-property-decorator';
import PurchaseListItemRow from '@/components/purchase-order/PurchaseListItemRow.vue';
import { purchaseList, PURCHASE_LIST_UPDATED } from '@/collections/purchase_list';
import { EventBus } from '@/eventBus';
import type { PurchaseListItem } from '@/types/purchase-list';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';

const OPEN = 'open-purchase-list-drawer';
const CLOSE = 'close-purchase-list-drawer';

@Component({ components: { PurchaseListItemRow } })
export default class PurchaseListDrawer extends Vue {
  public visible = false;
  public loading = false;
  public listRef = purchaseList;

  public created(): void {
    EventBus.$on(OPEN, this.open);
    EventBus.$on(CLOSE, this.close);
    EventBus.$on(PURCHASE_LIST_UPDATED, this.onListUpdated);
  }

  public beforeDestroy(): void {
    EventBus.$off(OPEN, this.open);
    EventBus.$off(CLOSE, this.close);
    EventBus.$off(PURCHASE_LIST_UPDATED, this.onListUpdated);
    document.body.classList.remove('purchase-list-drawer-open');
  }

  @Watch('visible')
  public onVisibleChange(open: boolean): void {
    document.body.classList.toggle('purchase-list-drawer-open', open);
    EventBus.$emit('purchase-list-drawer-visible', open);
  }

  public get total(): number {
    return Object.values(this.listRef.groups)
      .reduce((n, g) => n + g.items.length, 0);
  }

  public get isEmpty(): boolean {
    return this.total === 0;
  }

  public get totalQuantity(): number {
    return Object.values(this.listRef.groups)
      .reduce(
        (sum, g) => sum + g.items.reduce((s, i) => s + Number(i.quantity), 0),
        0,
      );
  }

  public formatGroupSubtotal(group: { items: PurchaseListItem[]; currencyCode?: string }): string {
    const sum = group.items.reduce(
      (s, i) => s + Number(i.quantity) * Number(i.unitCost), 0,
    );
    return CurrencyHelper.formatCurrencyWithCode(sum, group.currencyCode || 'USD');
  }

  public async open(): Promise<void> {
    this.visible = true;
    this.loading = true;
    try {
      await this.listRef.fetch();
    } finally {
      this.loading = false;
    }
  }

  public close(): void {
    this.visible = false;
  }

  public onListUpdated(): void {
    if (this.visible) {
      void this.listRef.fetch();
    }
  }

  public async onQtyChange(item: PurchaseListItem, quantity: number): Promise<void> {
    if (quantity <= 0) {
      this.removeItemLocal(item);
      await purchaseList.removeItem(item.id);
      return;
    }
    // Optimistic: mutate the reactive item so line total + group subtotal
    // recompute immediately; the API call + refetch follow.
    const previous = Number(item.quantity);
    item.quantity = quantity;
    try {
      await purchaseList.updateItem(item.id, { quantity });
    } catch {
      item.quantity = previous;
      EventBus.$emit('show-snackbar', {
        message: 'Failed to update quantity',
        color: 'error',
      });
    }
  }

  public async onRemove(item: PurchaseListItem): Promise<void> {
    this.removeItemLocal(item);
    await purchaseList.removeItem(item.id);
  }

  public async clearAll(): Promise<void> {
    await purchaseList.clearAll();
  }

  public goToSuppliers(): void {
    if (this.$route.path !== '/suppliers') {
      this.$router.push('/suppliers').catch(() => { /* ignore duplicate nav */ });
    }
    this.close();
  }

  public reviewOrder(): void {
    if (this.$route.path !== '/purchase-orders/purchase-list/review') {
      this.$router.push('/purchase-orders/purchase-list/review').catch(() => { /* ignore */ });
    }
    this.close();
  }

  private removeItemLocal(item: PurchaseListItem): void {
    for (const group of Object.values(this.listRef.groups)) {
      const idx = group.items.findIndex((i) => i.id === item.id);
      if (idx !== -1) {
        group.items.splice(idx, 1);
        return;
      }
    }
  }
}
